'use client'

import Link from 'next/link'
import { useState, useRef, useEffect } from 'react'
import { Globe, ChevronDown } from 'lucide-react'
import { PUBLIC_LOCALES, LOCALE_LABEL, LOCALE_SHORT, LOCALE_HOME, type PublicLocale } from '@/i18n/locales-meta'

interface Props {
  current: PublicLocale
}

/**
 * Public-site language switcher (DE / EN / TR). Renders a compact pill
 * showing the active locale; clicking opens a small menu that links to
 * each locale's homepage. Uses real <Link>s (hreflang set) so it works
 * without JS and is SEO-friendly, with a JS dropdown for nicer UX.
 */
export function LanguageSwitcher({ current }: Props) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(v => !v)}
        aria-label="Sprache wählen / Choose language"
        className="inline-flex items-center gap-1 rounded-full border border-pub-line px-2.5 py-1.5 text-[13px] font-medium text-pub-muted hover:text-pub-ink hover:border-pub-muted transition-colors">
        <Globe className="h-3.5 w-3.5" />
        {LOCALE_SHORT[current]}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 w-36 overflow-hidden rounded-xl border border-pub-line bg-white shadow-xl">
          {PUBLIC_LOCALES.map((l) => (
            <Link key={l} href={LOCALE_HOME[l]} hrefLang={l} onClick={() => setOpen(false)}
              className={`block px-3 py-2 text-[13px] transition-colors ${
                l === current ? 'bg-pub-accent/10 font-semibold text-pub-accent' : 'text-pub-ink hover:bg-neutral-50'
              }`}>
              {LOCALE_LABEL[l]}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
